import * as amplitude from '@amplitude/analytics-browser';
import { mapKeys, snakeCase } from 'lodash';

const isProd = process.env.VUE_APP_NODE_ENV === 'production';
const releaseStage = isProd ? 'production' : 'development';

// These are PUBLIC keys, not secret
// Adding behind .env to prevent keys from being exposed anyways
const amplitudeApiKeys = {
    production: process.env.VUE_APP_AMPLITUDE_PRODUCTION_API_KEY || '',
    // Add key only to verify events in development
    development: process.env.VUE_APP_AMPLITUDE_DEVELOPMENT_API_KEY || '',
};

const baseEventProperties = {
    platform: 'web',
    release_stage: releaseStage,
};

// Store router reference for automatic page_key injection
let routerInstance = null;

/**
 * Set the router instance for automatic page_key injection
 * @param {Object} router - Vue Router instance
 */
function setRouter(router) {
    routerInstance = router;
}

const componentType = {
    unknown: 'unknown',
    app: 'app',
    api: 'api',
    banner: 'banner',
    button: 'button',
    card: 'card',
    chart: 'chart',
    checkbox: 'checkbox',
    contentScript: 'content_script',
    dropdown: 'dropdown',
    icon: 'icon',
    link: 'link',
    menu: 'menu',
    modal: 'modal',
    numpad: 'numpad',
    page: 'page',
    sidebar: 'sidebar',
    searchBar: 'search_bar',
    serviceWorker: 'service_worker',
    switch: 'switch',
    table: 'table',
    text: 'text',
    textInput: 'text_input',
    tray: 'tray',
    video: 'video',
    widget: 'widget',
};

const actionType = {
    unknown: 'unknown',
    api: 'api',
    blur: 'blur',
    cancel: 'cancel',
    click: 'click',
    change: 'change',
    dismiss: 'dismiss',
    focus: 'focus',
    hover: 'hover',
    keyPress: 'key_press',
    measurement: 'measurement',
    move: 'move',
    process: 'process',
    render: 'render',
    search: 'search',
    select: 'select',
    submit: 'submit',
    scroll: 'scroll',
    view: 'view',
};

/**
 * Analytics utility for tracking events throughout the application
 */
export default {
    /**
     * Initialize Amplitude Analytics
     */
    initAmplitude() {
        const apiKey = amplitudeApiKeys[releaseStage];
        if (!apiKey) {
            console.warn(`Amplitude API key not set for release stage: ${releaseStage}`);
            return;
        }

        amplitude.init(apiKey, undefined, {
            autocapture: false,
            logLevel: isProd ? amplitude.Types.LogLevel.Warn : amplitude.Types.LogLevel.Debug,
            // TODO: Remove minIdLength when BE supports User UUIDs
            minIdLength: 1,
        });
    },

    /**
     * Track a custom event
     * @param {string} eventName - The name of the event
     * @param {Object} eventProperties - Optional properties to include with the event
     * @param {Object} options - Optional configuration for the event
     */
    trackEvent(eventName, eventProperties = {}, options = {}) {
        const enhancedProperties = {
            ...eventProperties,
            page_key: routerInstance?.currentRoute?.meta?.pageKey || undefined,
            page_path: routerInstance?.currentRoute?.path || undefined,
            page_path_full: routerInstance?.currentRoute?.fullPath || undefined,
        };

        // Ensure all event property keys are snake_case
        const snakeCasedEventProperties = mapKeys(enhancedProperties, (_, key) => snakeCase(key));

        amplitude.track(eventName, {
            ...snakeCasedEventProperties,
            // Avoid overwriting base event properties
            ...baseEventProperties,
        }, options);
    },

    /**
     * Set user properties
     * @param {Object} userProperties - Properties to set for the user
     * @param {Object} eventOptions - Optional configuration for the identify call
     */
    setUserProperties(userProperties = {}, eventOptions) {
        const identify = new amplitude.Identify();

        for (const [key, value] of Object.entries(userProperties)) {
            if (value !== undefined && value !== null && value !== '') {
                identify.set(key, value);
            }
        }

        amplitude.identify(identify, eventOptions);
    },

    /**
     * Track user login
     * @param {Object} user - User object with relevant properties
     * @param {string} loginMethod - Method used for login (e.g., 'email', 'username', 'unknown')
     */
    trackUserLogin(user, loginMethod) {
        amplitude.setUserId(user.id);

        this.setUserProperties({
            role: user.role,
            company_id: user.company?.id,
        });

        this.trackEvent('user_logged_in', {
            login_method: loginMethod,
        });
    },

    /**
     * Track user logout
     */
    trackUserLogout() {
        this.trackEvent('user_logged_out');

        // Reset amplitude with delay to ensure logged out event is sent
        setTimeout(() => {
            amplitude.reset();
        }, 0)
    },

    /**
     * Track button clicks
     * @param {string} componentName - Name of Vue component emitting this event
     * @param {string} buttonName - Name/label of the button
     * @param {Object} eventProperties - Optional properties to include with the event
     */
    trackButtonClick(componentName, buttonName, eventProperties = {}) {
        this.trackEvent('button_clicked', {
            ...eventProperties,
            button_name: snakeCase(buttonName),
            component_name: componentName,
            component_type: componentType.button,
            action_type: actionType.click,
        });
    },

    /**
     * Track link clicks
     * @param {string} componentName - Name of Vue component emitting this event
     * @param {string} linkName - Name/label of the link
     * @param {Object} eventProperties - Optional properties to include with the event
     */
    trackLinkClick(componentName, linkName, eventProperties = {}) {
        this.trackEvent('link_clicked', {
            ...eventProperties,
            link_name: snakeCase(linkName),
            component_name: componentName,
            component_type: componentType.link,
            action_type: actionType.click,
        });
    },

    /**
     * Track menu action clicks
     * @param {string} componentName - Name of Vue component emitting this event
     * @param {string} menuActionName - Name/label of the menu action
     * @param {Object} eventProperties - Optional properties to include with the event
     */
    trackMenuActionClick(componentName, menuActionName, eventProperties = {}) {
        this.trackEvent('menu_action_clicked', {
            ...eventProperties,
            menu_action_name: snakeCase(menuActionName),
            component_name: componentName,
            component_type: componentType.menu,
            action_type: actionType.click,
        });
    },

    /**
     * Track click events (links, cards, etc.)
     * @param {string} componentName - Name of Vue component emitting this event
     * @param {string} uiName - Name/label of the UI element
     * @param {string} componentType - Type of the component (e.g., 'link', 'card')
     * @param {Object} eventProperties - Optional properties to include with the event
     */
    trackClick(componentName, uiName, componentType, eventProperties = {}) {
        this.trackEvent('ui_clicked', {
            ...eventProperties,
            ui_name: snakeCase(uiName),
            component_name: componentName,
            component_type: componentType,
            action_type: actionType.click,
        });
    },

    /**
     * Track page views
     * Should ideally be called in mounted() lifecycle hook
     * @param {string} componentName - Name of Vue component emitting this event
     * @param {Object} eventProperties - Optional properties to include with the event
     */
    trackPageView(componentName, eventProperties = {}) {
        this.trackEvent('page_viewed', {
            ...eventProperties,
            component_name: componentName,
            component_type: componentType.page,
            action_type: actionType.view,
        });
    },

    /**
     * Track modal views
     * Should ideally be called in mounted() lifecycle hook
     * @param {string} componentName - Name of Vue component emitting this event
     * @param {Object} eventProperties - Optional properties to include with the event
     */
    trackModalView(componentName, eventProperties = {}) {
        this.trackEvent('modal_viewed', {
            ...eventProperties,
            component_name: componentName,
            component_type: componentType.modal,
            action_type: actionType.view,
        });
    },

    /**
     * Track video plays
     * @param {string} componentName - Name of Vue component emitting this event
     * @param {string} videoId - Identifier for the video being played
     * @param {Object} eventProperties - Optional properties to include with the event
     */
    trackVideoPlay(componentName, videoId, eventProperties = {}) {
        this.trackEvent('video_played', {
            ...eventProperties,
            video_id: videoId,
            component_name: componentName,
            component_type: componentType.video,
            action_type: actionType.play,
        });
    },

    /**
     * Set the router instance for automatic page_key injection
     * @param {Object} router - Vue Router instance
     */
    setRouter(router) {
        setRouter(router);
    },

    componentType,
    actionType,
};